import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import type { Folder } from '@/api';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { dropMessages, dropRefusal, useMessageDrag, type DropMode, type MessageDrag } from './messageDrag';

/**
 * Ctrl (or ⌥ / Alt) held copies instead of moving (ADR 0108), the platform
 * convention for file managers. Read on every `dragover` and on the drop,
 * since the key can be pressed or released mid-drag.
 */
export function dropModeOf(event: { ctrlKey: boolean; altKey: boolean }): DropMode {
  return event.ctrlKey || event.altKey ? 'copy' : 'move';
}

/** How long a dragged row has to rest on a collapsed row before it opens (doc 06). */
export const HOVER_EXPAND_MS = 600;

export interface DropTargetProps {
  onDragEnter: (event: DragEvent<HTMLElement>) => void;
  onDragOver: (event: DragEvent<HTMLElement>) => void;
  onDragLeave: (event: DragEvent<HTMLElement>) => void;
  onDrop: (event: DragEvent<HTMLElement>) => void;
}

/**
 * Hover-to-open for a collapsed tree row while a message drag is live.
 *
 * `dragenter`/`dragleave` fire for every descendant the pointer crosses, so a
 * counter (the composer's pattern) says whether the pointer is still on the
 * row. The timer is cleared on leave, on drop and when the drag ends; the row
 * only ever opens, never closes, so resting on an open row does nothing.
 */
function useHoverExpand(active: boolean, collapsed: boolean, onExpand: (() => void) | undefined) {
  const depth = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const clear = useCallback(() => {
    if (timer.current !== undefined) clearTimeout(timer.current);
    timer.current = undefined;
  }, []);
  // The drag ended (dropped, cancelled with Escape, or its row unmounted).
  useEffect(() => {
    if (active) return undefined;
    depth.current = 0;
    clear();
    return undefined;
  }, [active, clear]);
  useEffect(() => clear, [clear]);

  const enter = useCallback(() => {
    depth.current += 1;
    if (depth.current !== 1 || !collapsed || onExpand === undefined) return;
    clear();
    timer.current = setTimeout(() => {
      timer.current = undefined;
      onExpand();
    }, HOVER_EXPAND_MS);
  }, [clear, collapsed, onExpand]);
  const leave = useCallback(() => {
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) clear();
  }, [clear]);
  const reset = useCallback(() => {
    depth.current = 0;
    clear();
  }, [clear]);
  return { enter, leave, reset, inside: () => depth.current > 0 };
}

/**
 * A folder row as a drop target for dragged message rows (#168).
 *
 * Handlers exist only while a row drag is live: with none, nothing on the
 * page changes (the composer's file drop is untouched). A folder that refuses
 * the drag (`dropRefusal`) never calls `preventDefault` on `dragover`, which is
 * what makes the browser show its no-drop cursor and decline the drop.
 */
export function useFolderDrop(
  folder: Folder,
  { collapsed, onExpand }: { collapsed: boolean; onExpand?: () => void },
): { isTarget: boolean; props: DropTargetProps | undefined } {
  const drag = useMessageDrag();
  const online = useOnlineStatus();
  const accepts = drag !== null && dropRefusal(drag, folder, { online }) === undefined;
  const hover = useHoverExpand(drag !== null, collapsed, onExpand);
  // Which drag the pointer is over this row with: a highlight belongs to one
  // drag, so a new one never starts out lit on a row it has not reached.
  const [overDrag, setOverDrag] = useState<MessageDrag | null>(null);

  if (drag === null) return { isTarget: false, props: undefined };
  return {
    isTarget: accepts && overDrag === drag,
    props: {
      onDragEnter: (event) => {
        hover.enter();
        setOverDrag(drag);
        if (accepts) event.preventDefault();
      },
      onDragOver: (event) => {
        if (!accepts) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = dropModeOf(event);
      },
      onDragLeave: () => {
        hover.leave();
        if (!hover.inside()) setOverDrag(null);
      },
      onDrop: (event) => {
        hover.reset();
        setOverDrag(null);
        if (!accepts) return;
        event.preventDefault();
        dropMessages(folder, { mode: dropModeOf(event), online });
      },
    },
  };
}

/**
 * A mailbox row in the multi-mailbox sidebar: it takes no drop (a mailbox is
 * not a folder), but opens after the same rest so a row from the unified list
 * can reach its own mailbox's folders.
 */
export function useHoverToOpen(
  collapsed: boolean,
  onExpand: () => void,
): Omit<DropTargetProps, 'onDragOver'> | undefined {
  const drag = useMessageDrag();
  const hover = useHoverExpand(drag !== null, collapsed, onExpand);
  if (drag === null) return undefined;
  return {
    onDragEnter: () => hover.enter(),
    onDragLeave: () => hover.leave(),
    onDrop: () => hover.reset(),
  };
}
